/**
 * A readable diff of two versions (plan E02-T05): both as the YAML the builder shows, and only the
 * changed lines with a little context around them, the way a code review shows a change.
 */
import { structuredPatch } from "diff";
import { useMemo } from "react";
import { useT } from "../../i18n";
import { SpecDocument } from "../builder/specDocument";

const CONTEXT = 3;

export function SpecDiff({ before, after, label }: { before: unknown | null; after: unknown; label: string }) {
  const t = useT();
  const hunks = useMemo(() => {
    const a = before === null ? "" : SpecDocument.fromValue(before).text;
    const b = SpecDocument.fromValue(after).text;
    return structuredPatch("before", "after", a, b, "", "", { context: CONTEXT }).hunks;
  }, [before, after]);
  const added = hunks.reduce((n, h) => n + h.lines.filter((l) => l.startsWith("+")).length, 0);
  const removed = hunks.reduce((n, h) => n + h.lines.filter((l) => l.startsWith("-")).length, 0);

  return (
    <figure className="space-y-2">
      <figcaption className="text-sm text-[var(--color-muted)]">
        {hunks.length ? t("diff.summary", { added, removed }) : t("diff.none")}
      </figcaption>
      {hunks.length > 0 && (
        <div role="region" aria-label={label} tabIndex={0}
          className="max-h-96 overflow-auto rounded-md border border-[var(--color-border)] font-mono text-xs">
          {hunks.map((hunk, i) => (
            <div key={i} className="border-b border-[var(--color-border)] last:border-b-0">
              <div className="bg-[var(--color-surface)] px-2 py-0.5 text-[var(--color-muted)]">
                {t("diff.lines", { line: hunk.newStart })}
              </div>
              {hunk.lines.map((line, j) => {
                const sign = line[0];
                const style =
                  sign === "+" ? "bg-[var(--color-added)]" : sign === "-" ? "bg-[var(--color-removed)]" : "";
                return (
                  <div key={j} className={`whitespace-pre-wrap px-2 ${style}`}>
                    <span aria-hidden="true">{sign === " " ? " " : sign === "-" ? "−" : "+"} </span>
                    {sign !== " " && <span className="sr-only">{t(sign === "+" ? "diff.added" : "diff.removed")}: </span>}
                    {line.slice(1)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </figure>
  );
}
